import { forwardRef, useId } from 'react'
import type { InputHTMLAttributes, ReactNode } from 'react'
import { cn } from '@/lib/utils'

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  helperText?: string
  leftIcon?: ReactNode
  rightAdornment?: ReactNode
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, label, error, helperText, leftIcon, rightAdornment, id, type, ...rest },
  ref,
) {
  const autoId = useId()
  const inputId = id ?? autoId
  const describedById = error ? `${inputId}-error` : helperText ? `${inputId}-help` : undefined

  return (
    <div className="flex w-full flex-col gap-1.5">
      {label && (
        <label htmlFor={inputId} className="text-sm font-medium text-slate-800">
          {label}
        </label>
      )}
      <div className="relative">
        {leftIcon && (
          <span
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            aria-hidden
          >
            {leftIcon}
          </span>
        )}
        <input
          ref={ref}
          id={inputId}
          type={type ?? 'text'}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedById}
          className={cn(
            'h-10 w-full rounded-md border bg-white px-3 text-sm text-slate-900 placeholder:text-slate-400',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1',
            'disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500',
            leftIcon && 'pl-9',
            rightAdornment && 'pr-10',
            error
              ? 'border-[color:var(--color-fitlab-red)] focus-visible:ring-[color:var(--color-fitlab-red)]'
              : 'border-slate-300 focus-visible:ring-[color:var(--color-fitlab-blue)] focus-visible:border-[color:var(--color-fitlab-blue)]',
            className,
          )}
          {...rest}
        />
        {rightAdornment && (
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400">
            {rightAdornment}
          </span>
        )}
      </div>
      {error && (
        <p
          id={`${inputId}-error`}
          className="text-xs font-medium text-[color:var(--color-fitlab-red)]"
        >
          {error}
        </p>
      )}
      {!error && helperText && (
        <p id={`${inputId}-help`} className="text-xs text-slate-500">
          {helperText}
        </p>
      )}
    </div>
  )
})
